// Change password (§13.7, §17.11): current + new password with the full
// rule set, confirmed by OTP, with the reference shown at the end.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import {
  requestProfileOtp,
  resendProfileOtp,
  validateChangePassword,
  verifyAndChangePassword,
} from '@/api/profile';
import { ApiError } from '@/api/client';
import type { RootState } from '@/store';
import Button from '@/components/ui/Button/Button';
import OtpSheet from '@/components/common/auth/OtpSheet';
import './profile.css';

type Step = 'FORM' | 'OTP' | 'DONE';

const ChangePassword = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.account.user);
  const [step, setStep] = useState<Step>('FORM');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authId, setAuthId] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(180);
  const [otpSessionKey, setOtpSessionKey] = useState(0);
  const [reference, setReference] = useState('');

  const submit = async () => {
    setError('');
    if (newPassword !== confirmPassword) {
      setError('The new passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await validateChangePassword(currentPassword, newPassword, { email: user?.email });
      const session = await requestProfileOtp();
      setAuthId(session.authId);
      setOtpExpiry(session.expiresInSeconds);
      setOtpSessionKey(Date.now());
      setStep('OTP');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not validate the password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpVerify = async (code: string) => {
    const { reference: ref } = await verifyAndChangePassword(authId, newPassword, code);
    setReference(ref);
    setStep('DONE');
  };

  const handleOtpResend = async () => {
    const { expiresInSeconds } = await resendProfileOtp(authId);
    return expiresInSeconds;
  };

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button
          type="button"
          className="nova-icon-btn"
          title="Back"
          aria-label="Back"
          onClick={() => navigate('/profile/settings')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Change password</h2>
      </div>

      {step === 'FORM' && (
        <>
          <div className="mb-3">
            <label className="form-label">Current password</label>
            <input
              type="password"
              className="form-control"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">New password</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="form-text">
              8 to 20 characters, with upper and lower case, a number and a special character. Not your email name or
              year of birth.
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Re-enter the new password</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {error && <div className="nova-limit-box">{error}</div>}
          <Button
            onClick={() => void submit()}
            isLoading={submitting}
            disabled={!currentPassword || !newPassword || !confirmPassword}
          >
            Continue
          </Button>
        </>
      )}

      {step === 'DONE' && (
        <div className="nova-profile-success">
          <FontAwesomeIcon icon={faCircleCheck} size="3x" className="nova-outcome-ok" />
          <h3>Password changed</h3>
          <p>
            Your password has been changed. Every other session has been signed out. Reference {reference}.
          </p>
          <Button onClick={() => navigate('/profile/settings')}>Done</Button>
        </div>
      )}

      <OtpSheet
        show={step === 'OTP'}
        approvalLine="Approve password change"
        sessionKey={otpSessionKey}
        expiresInSeconds={otpExpiry}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onClose={() => navigate('/profile/settings')}
      />
    </div>
  );
};

export default ChangePassword;
